/**
 * recordsUploads.js — the uploaded-files half of the records export (P2 Release 3, PROD-167).
 *
 * Pure and import-free, so scripts/check-p2-access.mjs can run it under node. recordsPackage
 * hands it the signer, the fetcher and the zip writer.
 *
 * ★★★ WHY THIS EXISTS. orella-api P2-8 decides POST /files/signed-urls PER PATH: a path the
 *   caller may not read maps to null in `urls` and is listed in `denied`, and the batch never
 *   fails as a whole. recordsPackage read `urls` only and skipped every null in silence, so an
 *   export that lost files to a refusal looked exactly like a complete one. Every file that is
 *   not in the package is now named, with the reason:
 *     denied      — the server refused this account access to the file (`denied`)
 *     unavailable — it could not be signed or downloaded (a null not in `denied`, a failed
 *                   batch, or a failed download)
 * ★ The text names each file the way the zip would have (case-<n>-<name>). It never carries
 *   the storage path — the path is a key, not a name the patient knows.
 */

export const EXCLUDED_FILE_NAME = 'EXCLUDED_FILES.txt'

const displayName = (u) => (u.caseNumber ? `case-${u.caseNumber}-${u.name}` : u.name)

/**
 * @param {object}   args
 * @param {{path: string, name: string, caseNumber?: any}[]} args.uploads
 * @param {(paths: string[]) => Promise<{urls: object, denied: string[]}>} args.signBatch
 * @param {(url: string) => Promise<{ok: boolean, arrayBuffer: () => Promise<ArrayBuffer>}>} args.fetchFile
 * @param {(name: string, buf: ArrayBuffer) => void} args.addFile
 * @param {(done: number, total: number) => void} [args.onProgress]
 * @returns {Promise<{name: string, reason: 'denied' | 'unavailable'}[]>} what was left out
 */
export async function gatherUploadedRecords({ uploads, signBatch, fetchFile, addFile, onProgress = () => {}, batchSize = 50 }) {
  const list = Array.isArray(uploads) ? uploads : []
  const urlMap = {}
  const deniedSet = new Set()
  const paths = list.map((u) => u.path)
  for (let i = 0; i < paths.length; i += batchSize) {
    const batch = paths.slice(i, i + batchSize)
    try {
      const res = await signBatch(batch)
      Object.assign(urlMap, (res && res.urls) || {})
      for (const p of (res && Array.isArray(res.denied) ? res.denied : [])) deniedSet.add(p)
    } catch (_) { /* this batch could not be signed — its files are reported as unavailable */ }
  }
  const excluded = []
  const said = new Set()
  const exclude = (u, reason) => {
    const name = displayName(u)
    const key = `${reason}\u0000${name}`
    if (said.has(key)) return
    said.add(key)
    excluded.push({ name, reason })
  }
  const seen = new Set()
  let done = 0
  for (const u of list) {
    const url = urlMap[u.path]
    if (!url) {
      exclude(u, deniedSet.has(u.path) ? 'denied' : 'unavailable')
    } else {
      let stored = false
      try {
        const r = await fetchFile(url)
        if (r && r.ok) {
          const buf = await r.arrayBuffer()
          let name = displayName(u)
          while (seen.has(name)) name = `dup-${Math.random().toString(36).slice(2, 6)}-${name}`
          seen.add(name)
          addFile(name, buf)
          stored = true
        }
      } catch (_) { /* a download that fails is reported below */ }
      if (!stored) exclude(u, 'unavailable')
    }
    done++
    onProgress(done, list.length)
  }
  return excluded
}

/** The text of EXCLUDED_FILES.txt, or null when nothing was left out. */
export function excludedFilesText(excluded) {
  const list = Array.isArray(excluded) ? excluded : []
  if (!list.length) return null
  const denied = list.filter((e) => e.reason === 'denied')
  const unavailable = list.filter((e) => e.reason !== 'denied')
  const lines = [
    'Files not included in this download',
    '',
    `${list.length} of your uploaded file${list.length === 1 ? ' is' : 's are'} not in this package.`,
  ]
  if (denied.length) {
    lines.push('', 'Refused by the server (this account was denied access to the file):')
    for (const e of denied) lines.push(`  - ${e.name}`)
  }
  if (unavailable.length) {
    lines.push('', 'Could not be downloaded (try the export again later):')
    for (const e of unavailable) lines.push(`  - ${e.name}`)
  }
  lines.push('', 'If a file you expected is missing, contact Orella support.', '')
  return lines.join('\n')
}
